import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';

const CarScene3D = () => {
  const mountRef = useRef(null);
  
  useEffect(() => {
    // Scene setup
    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#000000');
    
    // Camera setup
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.set(0, 5, 10);
    camera.lookAt(0, 0, 0);
    
    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.shadowMap.enabled = true;
    
    // Add renderer to DOM
    mountRef.current.appendChild(renderer.domElement);
    
    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 10, 7.5);
    directionalLight.castShadow = true;
    scene.add(directionalLight);
    
    // Add accent light with red color for ZoneX branding
    const redSpotlight = new THREE.SpotLight(0xFC3F3F, 2);
    redSpotlight.position.set(-5, 10, 0);
    redSpotlight.angle = Math.PI / 6;
    redSpotlight.penumbra = 0.3;
    scene.add(redSpotlight);
    
    // Create ground/parking lot
    const groundGeometry = new THREE.PlaneGeometry(30, 30, 30, 30);
    const groundMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x333333,
      roughness: 0.8,
    });
    const ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);
    
    // Add parking lot markings
    const addParkingSpot = (x, z) => {
      const lineGeometry = new THREE.PlaneGeometry(2.5, 5);
      const lineMaterial = new THREE.MeshBasicMaterial({ 
        color: 0xFFFFFF,
        side: THREE.DoubleSide,
      });
      const parkingSpot = new THREE.Mesh(lineGeometry, lineMaterial);
      parkingSpot.rotation.x = -Math.PI / 2;
      parkingSpot.position.set(x, 0.01, z);
      scene.add(parkingSpot);
      
      // Add parking spot number
      const text = createText("P", 0.5);
      text.position.set(x, 0.02, z);
      text.rotation.x = -Math.PI / 2;
      scene.add(text);
    };
    
    // Create several parking spots
    for (let i = -10; i <= 10; i += 5) {
      addParkingSpot(i, 5);
      addParkingSpot(i, -5);
    }
    
    // Create simple car mesh function
    const createCar = (color, x, z, rotation) => {
      const carGroup = new THREE.Group();
      
      // Car body
      const bodyGeometry = new THREE.BoxGeometry(2, 0.5, 4);
      const bodyMaterial = new THREE.MeshStandardMaterial({ color });
      const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
      body.position.y = 0.5;
      body.castShadow = true;
      carGroup.add(body);
      
      // Car top
      const topGeometry = new THREE.BoxGeometry(1.5, 0.5, 2);
      const topMaterial = new THREE.MeshStandardMaterial({ color });
      const top = new THREE.Mesh(topGeometry, topMaterial);
      top.position.y = 1;
      top.position.z = -0.5;
      top.castShadow = true;
      carGroup.add(top);
      
      // Car wheels
      const wheelGeometry = new THREE.CylinderGeometry(0.4, 0.4, 0.2, 32);
      const wheelMaterial = new THREE.MeshStandardMaterial({ color: 0x222222 });
      
      // Front left wheel
      const flWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
      flWheel.rotation.z = Math.PI / 2;
      flWheel.position.set(-1, 0.4, 1.2);
      carGroup.add(flWheel);
      
      // Front right wheel
      const frWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
      frWheel.rotation.z = Math.PI / 2;
      frWheel.position.set(1, 0.4, 1.2);
      carGroup.add(frWheel);
      
      // Rear left wheel
      const rlWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
      rlWheel.rotation.z = Math.PI / 2;
      rlWheel.position.set(-1, 0.4, -1.2);
      carGroup.add(rlWheel);
      
      // Rear right wheel
      const rrWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
      rrWheel.rotation.z = Math.PI / 2;
      rrWheel.position.set(1, 0.4, -1.2);
      carGroup.add(rrWheel);
      
      // Headlights
      const headlightGeometry = new THREE.SphereGeometry(0.2, 16, 16);
      const headlightMaterial = new THREE.MeshBasicMaterial({ color: 0xffffaa });
      
      const leftHeadlight = new THREE.Mesh(headlightGeometry, headlightMaterial);
      leftHeadlight.position.set(-0.7, 0.5, 2);
      carGroup.add(leftHeadlight);
      
      const rightHeadlight = new THREE.Mesh(headlightGeometry, headlightMaterial);
      rightHeadlight.position.set(0.7, 0.5, 2);
      carGroup.add(rightHeadlight);
      
      // Windows (simplified)
      const windowMaterial = new THREE.MeshPhysicalMaterial({ 
        color: 0x444444,
        transparent: true,
        opacity: 0.7,
        metalness: 0.9,
        roughness: 0.1
      });
      
      const frontWindowGeometry = new THREE.PlaneGeometry(1.4, 0.4);
      const frontWindow = new THREE.Mesh(frontWindowGeometry, windowMaterial);
      frontWindow.position.set(0, 1, 0.5);
      frontWindow.rotation.x = Math.PI / 4;
      carGroup.add(frontWindow);
      
      // Position the car
      carGroup.position.set(x, 0, z);
      carGroup.rotation.y = rotation;
      
      return carGroup;
    };
    
    // Create text helper function
    function createText(text, size) {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = 256;
      canvas.height = 256;
      
      // Fill background transparent
      context.fillStyle = 'transparent';
      context.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw text
      context.font = 'Bold 100px Arial';
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillStyle = '#FFFFFF';
      context.fillText(text, canvas.width / 2, canvas.height / 2);
      
      // Create texture
      const texture = new THREE.CanvasTexture(canvas);
      
      // Create material
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        side: THREE.DoubleSide
      });
      
      // Create mesh
      const geometry = new THREE.PlaneGeometry(size, size);
      const mesh = new THREE.Mesh(geometry, material);
      
      return mesh;
    }
    
    // Create mechanic area
    const createMechanicArea = () => {
      const group = new THREE.Group();
      
      // Mechanic platform
      const platformGeometry = new THREE.BoxGeometry(8, 0.2, 8);
      const platformMaterial = new THREE.MeshStandardMaterial({ color: 0x444444 });
      const platform = new THREE.Mesh(platformGeometry, platformMaterial);
      platform.position.y = 0.1;
      platform.receiveShadow = true;
      group.add(platform);
      
      // Mechanic sign
      const sign = createText("MECHANIC", 2);
      sign.position.set(0, 2, -3);
      group.add(sign);
      
      // Tool box
      const toolboxGeometry = new THREE.BoxGeometry(1, 0.5, 0.5);
      const toolboxMaterial = new THREE.MeshStandardMaterial({ color: 0xFC3F3F });
      const toolbox = new THREE.Mesh(toolboxGeometry, toolboxMaterial);
      toolbox.position.set(2, 0.25, 2);
      toolbox.castShadow = true;
      group.add(toolbox);
      
      // Car lift (simplified)
      const liftBaseGeometry = new THREE.BoxGeometry(3, 0.2, 5);
      const liftBaseMaterial = new THREE.MeshStandardMaterial({ color: 0x222222 });
      const liftBase = new THREE.Mesh(liftBaseGeometry, liftBaseMaterial);
      liftBase.position.set(0, 0.2, 0);
      group.add(liftBase);
      
      return group;
    };
    
    // Add cars to the scene
    const car1 = createCar(0x5C6BC0, -8, 5, 0); // Blue car
    const car2 = createCar(0x66BB6A, -3, 5, 0); // Green car
    const car3 = createCar(0xFC3F3F, 8, -5, 0); // ZoneX red car
    scene.add(car1, car2, car3);
    
    // Create moving car
    const movingCar = createCar(0xFFC107, -15, 0, Math.PI / 2); // Yellow car
    scene.add(movingCar);
    
    // Add mechanic area
    const mechanicArea = createMechanicArea();
    mechanicArea.position.set(8, 0, 8);
    scene.add(mechanicArea);
    
    // Car on lift
    const carOnLift = createCar(0x9C27B0, 8, 8, 0); // Purple car
    carOnLift.position.y = 1; // Elevated on the lift
    scene.add(carOnLift);
    
    // Add ZoneX branding
    const zonexLogo = createText("ZoneX", 3);
    zonexLogo.position.set(0, 4, -12);
    zonexLogo.rotation.x = Math.PI / 8; // Tilt slightly
    scene.add(zonexLogo);
    
    // Animation variables
    let movingCarDirection = 1;
    let carOnLiftDirection = 1;
    let carLiftHeight = 1;
    
    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      
      // Move the car
      movingCar.position.x += 0.05 * movingCarDirection;
      
      // Change direction when reaching bounds
      if (movingCar.position.x > 15) {
        movingCarDirection = -1;
        movingCar.rotation.y = -Math.PI / 2;
      } else if (movingCar.position.x < -15) {
        movingCarDirection = 1;
        movingCar.rotation.y = Math.PI / 2;
      }
      
      // Animate car on lift
      carLiftHeight += 0.01 * carOnLiftDirection;
      if (carLiftHeight > 1.5) carOnLiftDirection = -1;
      if (carLiftHeight < 1) carOnLiftDirection = 1;
      carOnLift.position.y = carLiftHeight;
      
      // Rotate the camera slightly for more dynamic view
      camera.position.x = 10 * Math.sin(Date.now() * 0.0005);
      camera.position.z = 10 * Math.cos(Date.now() * 0.0005);
      camera.lookAt(0, 0, 0);
      
      renderer.render(scene, camera);
    };
    
    // Handle window resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Start animation
    animate();
    
    // Clean up on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      mountRef.current && mountRef.current.removeChild(renderer.domElement);
    };
  }, []);
  
  return (
    <div 
      ref={mountRef} 
      className="absolute inset-0 -z-10 bg-black"
      style={{ height: '100vh' }}
    />
  );
};

export default CarScene3D;